/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { CustomIcon, OnlyInMobile } from '@components/shared';
import renderingInServer from '@shared/renderingInServer';
import routes from '@root/routes';
import { hasCurrentUserLoaded } from '@root/store/selectors';
import fetchCurrentUserAction, { updateCurrentUser } from '@store/actions/currentUser';
import { Pages, StoreState, Locale } from '@root/types';
import navActions from '@store/actions/navigation';
import actions from '@store/actions';
import classnames from 'classnames';
import useAnalytics from '@shared/useAnalytics';
import useEnv from '@shared/useEnv';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { NavLink } from 'react-router-dom';
import Logo from './Logo';
import BuildingSearch from './BuildingSearch';
import DropdownMenu from './DropdownMenu';
import s from './Navigation.module.less';
import LocaleSelector from '../LocaleSelection';

type Props = {
  activePage?: Pages;
};

const mapState = (state: StoreState) => ({
  currentUser: state.currentUser,
  isCurrentUserLoaded: hasCurrentUserLoaded(state),
});
const mapDispatch = dispatch => ({
  updateUser: ({ user, onSuccess, onError, analyticsInfo }) =>
    dispatch(updateCurrentUser({ user, onSuccess, onError, ...analyticsInfo })),
  fetchCurrentUser: () => dispatch(fetchCurrentUserAction()),
  searchClicked: () => {
    dispatch(actions.setDrawnPolygons([]));
    dispatch(navActions.searchClicked());
  },
  tourbooksClicked: () => dispatch(navActions.tourbooksClicked),
});

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

function Navigation({
  activePage,
  currentUser,
  isCurrentUserLoaded,
  searchClicked,
  tourbooksClicked,
  updateUser,
  fetchCurrentUser,
}: Props & ReduxProps) {
  const { t, i18n } = useTranslation('navigation');
  const { vtsUrl } = useEnv();
  const { clickToPage, anonymousId, PARAMETERS } = useAnalytics();
  const cookieName = 'truva-language-preference';
  const [cookies, setCookie] = useCookies([cookieName]);

  const getLocalStorageLanguagePreference = (): Locale => {
    if (renderingInServer()) return 'en-us';

    const supportedLanguages: Locale[] = ['en-gb', 'en-us'];
    const defaultLanguage = 'en-us';
    const { [cookieName]: storedLanguagePreference } = cookies;
    const languagePreference = storedLanguagePreference || (i18n.language as Locale);

    return supportedLanguages.includes(languagePreference) ? languagePreference : defaultLanguage;
  };

  const tourbookPages: Pages[] = ['myTourbooks', 'tourbookExternalListing', 'tourbookShow'];
  const savedPages: Pages[] = [
    'savedBuildingOwners',
    'savedBuildings',
    'savedListings',
    'savedSearches',
  ];
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);

  const userLanguagePreference: Locale =
    currentUser?.languagePreference ?? getLocalStorageLanguagePreference();

  const setLocalStorageLanguagePreference = (languagePreference: Locale) => {
    setCookie(cookieName, languagePreference);
    window.location.reload();
  };

  const handleChangeLanguagePreference = async (languagePreference, analyticsInfo) => {
    await i18n.changeLanguage(languagePreference);
    if (currentUser) {
      updateUser({
        user: { ...currentUser, languagePreference },
        onSuccess: async () => {
          await fetchCurrentUser();
          window.location.reload();
        },
        onError: errorsObject => {
          // eslint-disable-next-line no-console
          console.error(errorsObject);
        },
        analyticsInfo,
      });
    } else {
      setLocalStorageLanguagePreference(languagePreference);
    }
  };

  return (
    <nav data-testid="nav" className={s.nav}>
      <OnlyInMobile>
        <button
          className={s.hamburgerMenu}
          type="button"
          onClick={() => setMobileOverlayOpen(true)}
        >
          <CustomIcon type="antdMenuOutlined" />
        </button>
      </OnlyInMobile>
      <Logo />
      {currentUser ? (
        <ul
          className={classnames(
            s.navLinkList,
            searchOpen && s.searchOpened,
            mobileOverlayOpen && s.mobileOverlayOpened,
          )}
        >
          <li className={s.mobileClose}>
            <Logo />
            <button
              className={s.closeButton}
              type="button"
              onClick={() => setMobileOverlayOpen(false)}
            >
              <CustomIcon type="close" />
            </button>
          </li>
          <li
            data-testid="searchLink"
            className={classnames(
              s.navigationLink,
              activePage === 'listingSearch' && s.active,
              s.listingSearchLink,
            )}
          >
            <NavLink to={routes.search(currentUser.marketSlug)} onClick={searchClicked}>
              <CustomIcon className={s.icon} type="magnifying-glass"></CustomIcon>
              <span className={s.navText}>{t('search')}</span>
            </NavLink>
          </li>
          <BuildingSearch searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
          <li
            className={classnames(
              s.navigationLink,
              // @ts-expect-error
              tourbookPages.includes(activePage) && s.active,
            )}
          >
            <NavLink to={routes.tourbooks} onClick={tourbooksClicked}>
              <div>
                <CustomIcon className={s.icon} type="tourbook"></CustomIcon>
                <span className={s.navText}>{t('tourbook')}</span>
              </div>
            </NavLink>
          </li>
          <li
            className={classnames(
              s.navigationLink,
              s.dropdownNav,
              // @ts-expect-error
              savedPages.includes(activePage) && s.active,
            )}
          >
            <NavLink to={routes.savedListings}>
              <div>
                <CustomIcon className={s.icon} type="heart-outline"></CustomIcon>
                <span className={s.navText}>{t('saved')}</span>
              </div>
            </NavLink>
            <DropdownMenu
              options={[
                {
                  label: t('listings'),
                  route: routes.savedListings,
                },
                {
                  label: t('searches'),
                  route: routes.savedSearches,
                },
                {
                  label: t('buildings'),
                  route: routes.savedBuildings,
                },
                {
                  label: t('buildingOwners'),
                  route: routes.savedBuildingOwners,
                },
              ]}
            />
          </li>
          <li
            className={classnames(
              s.navigationLink,
              s.dropdownNav,
              activePage === 'userAccountSettings' && s.active,
            )}
          >
            <NavLink to={routes.users.edit('professional_info')}>
              <div>
                <CustomIcon className={s.icon} type="person-circle"></CustomIcon>
                <span className={s.navText}>{currentUser?.firstName}</span>
              </div>
            </NavLink>
            <DropdownMenu
              options={[
                {
                  label: t('account'),
                  route: routes.users.edit('professional_info'),
                },
                ...(currentUser.role === 'admin'
                  ? [
                      {
                        label: t('admin'),
                        route: '/admin',
                        outsideLink: true,
                      },
                    ]
                  : []),
                {
                  label: t('vtsHome'),
                  route: vtsUrl!,
                  outsideLink: true,
                },
                {
                  label: t('signOut'),
                  route: '/logout',
                  outsideLink: true,
                },
              ]}
            />
          </li>
          <li className={classnames(s.localeSelector)}>
            <LocaleSelector
              userLanguagePreference={userLanguagePreference}
              handleChangeLanguage={handleChangeLanguagePreference}
            />
          </li>
        </ul>
      ) : isCurrentUserLoaded ? (
        <ul className={classnames(s.navLinkList, mobileOverlayOpen && s.mobileOverlayOpened)}>
          <li className={s.mobileClose}>
            <Logo />
            <button
              className={s.closeButton}
              type="button"
              onClick={() => setMobileOverlayOpen(false)}
            >
              <CustomIcon type="close" />
            </button>
          </li>
          <li className={classnames(s.navigationLink)}>
            <a
              href={routes.signUp(vtsUrl!, {
                utm_content: 'marketplace nav',
                anonymous_id: anonymousId,
              })}
              onClick={() => {
                clickToPage({
                  destination: PARAMETERS.signUpPage,
                  sourceContent: PARAMETERS.navigation,
                });
              }}
            >
              {t('signUp')}
            </a>
          </li>
          <li className={classnames(s.navigationLink)}>
            <a href="/login">{t('logIn')}</a>
          </li>
          <li className={classnames(s.localeSelector)}>
            <LocaleSelector
              userLanguagePreference={userLanguagePreference}
              handleChangeLanguage={handleChangeLanguagePreference}
            />
          </li>
        </ul>
      ) : null}
    </nav>
  );
}

export default connector(Navigation);
